/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {CacheEntry, CacheExpiry, CacheParameters, CacheScope, CacheSetParameters, CacheTTL} from '@adobe/exc-app/cache';
import {getBootstrap} from './BootstrapService';
import {getErrorMessage} from '@exc/shared';
import hashString from 'string-hash';
import metrics, {Level} from '@adobe/exc-app/metrics';
import type {MinimalCacheStorage} from '../utils/cache-mini-polyfill';
import {UserOrgContext} from '../models/Context';

interface AppCacheServiceParams {
  appId: string;
  context: UserOrgContext;
}

type CacheEntryWithSession<T> = CacheEntry<T> & {sid?: string};

const CLEANUP_DELAY = 300 * 1000;

const hashStr = (str: string) => String(hashString(str));

const getSid = (): string => getBootstrap().getSid() || 'unknown';
let cachesPolyfill: MinimalCacheStorage | undefined;

/**
 * Returns a hashed scope identifier (org / sandbox name / etc).
 * @param {CacheScope} scope - Scope
 * @param {UserOrgContext} context - Context object.
 * @returns {string} Hashed scope ID.
 * @private
 */
export const getScopeId = (scope: CacheScope, context: UserOrgContext): string => {
  const {imsProfile: {userId}, imsOrg, sandbox, subOrg} = context;
  switch (scope) {
    case CacheScope.ORG:
      return hashStr(`${imsOrg}-${userId}`);
    case CacheScope.SANDBOX:
      if (!sandbox) {
        throw new Error('Sandbox not available');
      }
      return hashStr(`${imsOrg}-${sandbox.name}-${userId}`);
    case CacheScope.SUBORG:
      if (!subOrg) {
        throw new Error('Suborg not available');
      }
      return hashStr(`${imsOrg}-${subOrg.name}-${userId}`);
    case CacheScope.USER:
    default: {
      return hashStr(userId);
    }
  }
};

const isExpired = (entry: CacheEntryWithSession<any>): boolean => {
  if ('expireBySession' in entry && entry.expireBySession && entry.sid !== getSid()) {
    return true;
  }

  return 'expiresAt' in entry && entry.expiresAt < Date.now();
};

class CacheService {
  private readonly cachePromises: Record<string, Promise<Cache>> = {};
  private readonly metrics = metrics.create('exc.core.services.AppCacheService');

  private async cleanCache(cache: Cache) {
    // Remove expired entries.
    let removed = 0;
    try {
      const keys = await cache.keys();
      for (const request of keys) {
        const response = await cache.match(request);
        if (response) {
          const entry = await response.clone().json() as CacheEntryWithSession<any>;
          if (isExpired(entry)) {
            await cache.delete(request);
            removed++;
          }
        }
      }
      removed && this.metrics.event('AppCache.Cleanup', {removed});
    } catch (err) {
      const error = getErrorMessage(err);
      this.metrics.event('AppCache.CleanupFailed', {error, removed}, {level: Level.ERROR});
    }
  }

  /**
   * Opens the named cache.
   * @param {string} name
   * @returns Cache - Named Cache object.
   * @private
   */
  private async openCache(name: string): Promise<Cache> {
    if (!this.cachePromises[name]) {
      if (!window.caches) {
        // No cache storage. Likely old browser or under test.
        // Use a memory based polyfill.
        if (!cachesPolyfill) {
          cachesPolyfill = (await import('../utils/cache-mini-polyfill')).default;
        }
        return cachesPolyfill.open(name);
      }
      this.cachePromises[name] = caches.open(name).then(cache => {
        setTimeout(() => this.cleanCache(cache), CLEANUP_DELAY);
        return cache;
      });
    }
    return this.cachePromises[name];
  }

  /**
   * Gets the cache path (Relative URL) for the cached key.
   * @param {string} key - Cache Entry Key
   * @param {string} appId - Current Application ID
   * @param {UserOrgContext} context - Context ojbect
   * @param {CacheScope} scope - Cache Scope
   * @returns {string} cache path.
   * @private
   */
  private getCacheEntryPath(key: string, appId: string, context: UserOrgContext, scope = CacheScope.DEFAULT): string {
    const scopeId = getScopeId(scope, context);
    return `/appCache/${appId}/${scope}/${scopeId}/${encodeURIComponent(key)}`;
  }

  /**
   * Deletes the cached entry (If exists).
   * @param {string} key - Cache Entry Key
   * @param {string} appId - Current Application ID
   * @param {UserOrgContext} context - Context ojbect
   * @param {CacheScope} scope - Cache Scope
   */
  async delete({appId, key, context, scope}: CacheParameters & AppCacheServiceParams): Promise<void> {
    try {
      const path = this.getCacheEntryPath(key, appId, context, scope);
      const cache = await this.openCache(`${appId}Cache`);
      await cache.delete(path);
    } catch (err) {
      const error = getErrorMessage(err);
      this.metrics.event('AppCache.DeleteFailed', {appId, error, key}, {level: Level.ERROR});
    }
  }

  /**
   * Returns a cached entry if availble.
   * @template T
   * @param {string} key - Cache Entry Key
   * @param {string} appId - Current Application ID
   * @param {UserOrgContext} context - Context ojbect
   * @param {CacheScope} scope - Cache Scope
   *
   * @returns {CacheEntry|undefined}
   */
  async get<T>({appId, key, context, scope}: CacheParameters & AppCacheServiceParams): Promise<CacheEntry<T> | undefined> {
    try {
      const path = this.getCacheEntryPath(key, appId, context, scope);
      const cache = await this.openCache(`${appId}Cache`);
      const request = await cache.match(path);

      if (request) {
        const entry = await request.clone().json() as CacheEntryWithSession<T>;
        if (!isExpired(entry)) {
          return entry;
        }
        await cache.delete(path);
      }
    } catch (err) {
      const error = getErrorMessage(err);
      this.metrics.event('AppCache.GetFailed', {appId, error, key}, {level: Level.ERROR});
    }
  }

  /**
   * Stores a value in the cache.
   * @template T
   * @param {CacheExpiry} expiry
   * @param {string} key - Cache Entry Key
   * @param {string} appId - Current Application ID
   * @param {UserOrgContext} context - Context ojbect
   * @param {T} value - Value to cache
   * @param {CacheScope} scope - Cache Scope
   */
  async set<T>({appId, expiry, key, context, value, scope}: CacheSetParameters<T> & AppCacheServiceParams): Promise<CacheEntry<T>|undefined> {
    try {
      const cacheExpiry: CacheExpiry = expiry || CacheTTL.DEFAULT;
      const path = this.getCacheEntryPath(key, appId, context, scope);
      const cache = await this.openCache(`${appId}Cache`);
      const createdAt = Date.now();
      const baseEntry = {createdAt, key, value};

      const entry: CacheEntryWithSession<T> = cacheExpiry === CacheTTL.IMS_SESSION ? {
        ...baseEntry, expireBySession: true, sid: getSid()
      } : {
        ...baseEntry, expiresAt: createdAt + cacheExpiry * 1000
      };

      await cache.put(path, new Response(JSON.stringify(entry), {headers: {'content-type': 'Application/json'}}));
      return entry;
    } catch (err) {
      const error = getErrorMessage(err);
      this.metrics.event('AppCache.SetFailed', {appId, error, key}, {level: Level.ERROR});
    }
  }
}

const cacheService = new CacheService();

export default cacheService;
